import { api } from '@/lib/api';

/** Matches the 10 MB cap the upload routes enforce server-side. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * Catalogue photography lives in private storage, so every URL the API returns
 * is a short-lived signed link. Nothing here caches one: a stale link is a
 * broken image, and re-fetching the row is cheap.
 */
const upload = async <T>(path: string, file: File): Promise<T> => {
  const body = new FormData();
  body.append('file', file);
  return api.post<T>(path, body);
};

export interface RoomTypeImages {
  id: string;
  images: string[];
  /** Stored key per signed photo, aligned with `images`. */
  imageKeys: string[];
}

export const uploadRoomTypeImage = (id: string, file: File) =>
  upload<RoomTypeImages>(`/room-types/${id}/images`, file);

/**
 * Photos are named by their stored key. The signed list a browser holds omits
 * anything that could not be signed, so its positions do not line up with what
 * the server stores — an index would delete the wrong photo.
 */
export const removeRoomTypeImage = (id: string, key: string) =>
  api.del<RoomTypeImages>(`/room-types/${id}/images`, { query: { key } });

export const uploadHallImage = (id: string, file: File) =>
  upload<RoomTypeImages>(`/conference-halls/${id}/images`, file);

export const removeHallImage = (id: string, key: string) =>
  api.del<RoomTypeImages>(`/conference-halls/${id}/images`, { query: { key } });

export const uploadPropertyLogo = (file: File) =>
  upload<{ logoUrl: string }>('/property-profile/logo', file);

export const uploadBuildingImage = (name: string, file: File) =>
  upload<{ buildingImages: Record<string, string> }>(
    `/property-profile/building-images/${encodeURIComponent(name)}`,
    file,
  );

export const removeBuildingImage = (name: string) =>
  api.del<{ buildingImages: Record<string, string> }>(
    `/property-profile/building-images/${encodeURIComponent(name)}`,
  );
